export type TrainingSection = 'overview' | 'library' | 'tracks' | 'assignments' | 'review' | 'learner' | 'transcript'

export function toDateInput(iso: string): string {
  return iso.slice(0, 10)
}

export function isExpired(isoDate: string): boolean {
  return new Date(isoDate).getTime() < Date.now()
}

export function readSectionFromPath(pathname: string): TrainingSection {
  const match = pathname.match(/\/training\/([^/]+)/)
  const candidate = match?.[1]
  switch (candidate) {
    case 'overview':
    case 'library':
    case 'tracks':
    case 'assignments':
    case 'review':
    case 'learner':
    case 'transcript':
      return candidate
    default:
      return 'overview'
  }
}

export function getDefaultSectionForViewer(role: string | undefined): TrainingSection {
  if (role === 'Staff') return 'learner'
  if (role === 'Reviewer') return 'review'
  return 'overview'
}

export function matchesViewerAssignmentRole(targetRole: string | null | undefined, viewerRole: string | undefined): boolean {
  if (!targetRole || !viewerRole) return false
  const normalizedTarget = targetRole.trim().toLowerCase()
  if (viewerRole === 'Staff') return normalizedTarget === 'user'
  if (viewerRole === 'Surveyor') return normalizedTarget === 'editor'
  if (viewerRole === 'Reviewer') return normalizedTarget === 'reviewer'
  if (viewerRole === 'Admin') return normalizedTarget === 'admin' || normalizedTarget === 'superadmin'
  return false
}

export function formatTrustActor(actor: { name?: string; credentials?: string; role?: string; status?: string } | null | undefined): string {
  if (!actor) return 'Pending'
  const parts = [actor.name, actor.credentials, actor.status].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : 'Pending'
}

export function stripTrainingTestSuffix(title: string | null | undefined): string {
  return String(title || '')
    .replace(/\s*\[E2E\s+\d{8,14}\]\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function formatTrainingModuleTitle(module: { title?: string | null } | null | undefined, fallback = 'Module pending title'): string {
  return stripTrainingTestSuffix(module?.title) || fallback
}

export function formatTrainingModuleOptionLabel(
  module: { title?: string | null; version_number?: number | null } | null | undefined,
  fallback = 'Module',
): string {
  const title = formatTrainingModuleTitle(module, fallback)
  return module?.version_number ? `${title} v${module.version_number}` : title
}

export function getTrainingContentBlockText(block: { text?: string | null } | string | null | undefined): string {
  if (typeof block === 'string') return block
  return block?.text || ''
}

export function cleanTrainingContentPreview(value: string | null | undefined): string {
  const lines = String(value || '')
    .replace(/\r/g, '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^---+$/.test(line))
    .filter((line) => !/^#{1,4}\s*FORMAT\s+\d+\b/i.test(line))
    .filter((line) => !/^(?:[-\u2014]\s*)?FORMAT\s+\d+\b/i.test(line))
    .filter((line) => !/^(PRESENTATION DECK|VOICEOVER|PODCAST|VIDEO SCRIPT|QUIZ)\b/i.test(line))
    .map((line) => line.replace(/^ASC Survey Training Module\s*(?:\u2014|-)\s*/i, ''))
    .filter((line) => !/^ASC Survey Training Module$/i.test(line))

  return lines
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 900)
}

export interface TrainingLessonPanelAsset {
  asset_id: string
  kind: string
  status: string
  storage_mode: string
  mime_type: string
}

export interface TrainingLessonPanel {
  id: string
  kind: 'slide' | 'asset' | 'summary'
  title: string
  body: string
  meta?: string
  asset?: TrainingLessonPanelAsset
}

type LessonAsset = {
  asset_id?: string | null
  kind?: string | null
  status?: string | null
  storage_ref?: string | null
  storage_mode?: string | null
  mime_type?: string | null
}

type LessonAssignment = {
  assignment_id?: string | null
  education_modules?: {
    title?: string | null
    content_blocks?: Array<{ type?: string | null; text?: string | null } | string>
    education_module_assets?: LessonAsset[]
  } | null
}

function cleanTrainingLessonText(value: string | null | undefined, maxLength = 1400): string {
  const lines = String(value || '')
    .replace(/\r/g, '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^---+$/.test(line))
    .filter((line) => !/^#{1,4}\s*FORMAT\s+\d+\b/i.test(line))
    .filter((line) => !/^(?:[-\u2014]\s*)?FORMAT\s+\d+\b/i.test(line))
    .filter((line) => !/^(PRESENTATION DECK|VOICEOVER|PODCAST|VIDEO SCRIPT|QUIZ)\b/i.test(line))
    .map((line) => line.replace(/^ASC Survey Training Module\s*(?:\u2014|-)\s*/i, ''))
    .filter((line) => !/^ASC Survey Training Module$/i.test(line))

  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, maxLength)
}

function normalizeSlideTitle(line: string): string {
  return line
    .replace(/^#{1,4}\s*/, '')
    .replace(/\s*[-\u2014]{1,2}\s*/, ': ')
    .replace(/\s+/g, ' ')
    .trim()
}

function parseSlidePanels(rawText: string, blockIndex: number): TrainingLessonPanel[] {
  const panels: TrainingLessonPanel[] = []
  const lines = rawText.replace(/\r/g, '').split('\n')
  let currentTitle = ''
  let currentBody: string[] = []

  const pushCurrentPanel = () => {
    if (!currentTitle) return
    const body = cleanTrainingLessonText(currentBody.join('\n'))
    panels.push({
      id: `slide-${blockIndex}-${panels.length + 1}`,
      kind: 'slide',
      title: normalizeSlideTitle(currentTitle),
      body: body || 'Slide content is being prepared for this module.',
    })
  }

  lines.forEach((line) => {
    if (/^#{1,4}\s*Slide\s+\d+\b/i.test(line.trim())) {
      pushCurrentPanel()
      currentTitle = line.trim()
      currentBody = []
      return
    }
    if (currentTitle) currentBody.push(line)
  })
  pushCurrentPanel()

  return panels
}

function formatAssetKind(kind: string | null | undefined): string {
  switch (kind) {
    case 'audio':
      return 'Audio'
    case 'video':
      return 'Video'
    case 'slides':
      return 'Slides'
    case 'quiz':
      return 'Quiz'
    case 'attachment':
      return 'Attachment'
    default:
      return 'Media'
  }
}

function describeGovernedAssetAccess(asset: LessonAsset, label: string): string {
  const status = String(asset.status || '').toLowerCase()
  if (status === 'approved') {
    if (asset.kind === 'slides') return 'Slides are ready for learner access. Review the deck, then continue through the quiz and attestation flow.'
    if (asset.kind === 'audio') return 'Audio narration is ready for learner access. Start playback from the approved audio control, then continue through the quiz and attestation flow.'
    if (asset.kind === 'quiz') return 'The bounded quiz is available in the quiz player. Complete the assigned subset rather than the full reviewed source bank.'
    return `${label} is ready for learner access.`
  }
  if (asset.kind === 'audio') return 'Audio narration is attached for review but is not approved for playback yet. Continue with slides, quiz, and attestation while audio generation remains in review.'
  if (asset.kind === 'slides') return 'Slides are attached as a deck derivative for review. Learner deck access remains controlled until the asset is approved.'
  if (asset.kind === 'quiz') return 'The reviewed quiz source is attached as metadata. Learner launch uses only an approved bounded quiz subset.'
  return `${label} is attached as metadata and remains pending reviewer approval.`
}

export function buildTrainingLessonPanels(assignment: LessonAssignment | null | undefined): TrainingLessonPanel[] {
  const contentBlocks = assignment?.education_modules?.content_blocks || []
  const rawTexts = contentBlocks.map((block) => getTrainingContentBlockText(block)).filter(Boolean)
  const slidePanels = rawTexts.flatMap((rawText, blockIndex) => parseSlidePanels(rawText, blockIndex))
  const panels: TrainingLessonPanel[] = slidePanels.length > 0 ? slidePanels : []

  if (panels.length === 0) {
    const summary = cleanTrainingLessonText(rawTexts.join('\n\n'))
    if (summary) {
      panels.push({
        id: 'summary-1',
        kind: 'summary',
        title: 'Lesson overview',
        body: summary,
      })
    }
  }

  const assets = assignment?.education_modules?.education_module_assets || []
  assets.forEach((asset, index) => {
    const label = formatAssetKind(asset.kind)
    const status = asset.status ? `Status: ${asset.status}` : 'Status: pending'
    panels.push({
      id: `asset-${asset.asset_id || index}`,
      kind: 'asset',
      title: `${label} access`,
      body: describeGovernedAssetAccess(asset, label),
      meta: [status, asset.storage_mode, asset.mime_type].filter(Boolean).join(' | '),
      asset: asset.asset_id
        ? {
          asset_id: asset.asset_id,
          kind: String(asset.kind || ''),
          status: String(asset.status || ''),
          storage_mode: String(asset.storage_mode || ''),
          mime_type: String(asset.mime_type || ''),
        }
        : undefined,
    })
  })

  if (panels.length === 0) {
    panels.push({
      id: 'empty-lesson',
      kind: 'summary',
      title: 'Lesson content pending',
      body: 'This assignment is ready for attestation, but lesson content has not been attached yet.',
    })
  }

  return panels
}
